import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createInitialGeoPointPinFeatureLayer } from '../helpers/feature-layers';
import { createInitialGeoPointPinGraphic } from '../helpers/graphics';
import { areLatLngsEqual } from '../../location/helpers';
import { GeoPoint } from '../../location/types';
import { GeoPointExtended } from '../types';
import { useLayerControl } from './use-feature-layer-control';

type InitialGeoPointPinFeatureLayerProps = {
    initialGeoPoint?: GeoPoint;
    webMap?: WebMap;
};

export const useInitialGeoPointPinFeatureLayer = ({
    initialGeoPoint,
    webMap,
}: InitialGeoPointPinFeatureLayerProps) => {
    const [initialGeoPointPinLayer, setInitialGeoPointPinLayer] = useState<FeatureLayer>();

    const entityIdToObjectIdMapCacheRef = useRef<
        typeof EntityIdToObjectIdMaps.INITIAL_GEO_POINT_PIN
    >(EntityIdToObjectIdMaps.INITIAL_GEO_POINT_PIN);

    const initialGeoPointExtended: GeoPointExtended[] = useMemo(
        () => (initialGeoPoint ? [{ ...initialGeoPoint, entityId: 1 }] : []),
        [initialGeoPoint]
    );

    const didInitialGeoPointGraphicChange = useCallback(
        (currItem: GeoPointExtended, prevItem: GeoPointExtended) => {
            return !areLatLngsEqual(currItem, prevItem);
        },
        []
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const featureLayer = createInitialGeoPointPinFeatureLayer();
            setInitialGeoPointPinLayer(featureLayer);
            webMap.layers.add(featureLayer);
        }
    }, [webMap]);

    const createInitialGeoPointPinGraphics = useCallback((geoPoints: GeoPointExtended[]) => {
        const graphics: Graphic[] = [];
        for (const geoPoint of geoPoints) {
            const objectId = entityIdToObjectIdMapCacheRef.current.get(geoPoint.entityId);
            const initialGeoPointPinGraphic = createInitialGeoPointPinGraphic({
                objectId,
                geoPoint,
            });
            if (initialGeoPointPinGraphic) {
                graphics.push(initialGeoPointPinGraphic);
            }
        }
        return graphics;
    }, []);

    useLayerControl<GeoPointExtended>({
        layer: initialGeoPointPinLayer,
        currItems: initialGeoPointExtended,
        didItemChange: didInitialGeoPointGraphicChange,
        createGraphics: createInitialGeoPointPinGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { initialGeoPointPinLayer };
};
